import { useState } from "react";
import { yearRanges } from "@/lib/movieData";

interface FilterSidebarProps {
  genres: string[];
  activeGenre: string;
  activeYear: string;
  sort: string;
  onGenreChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onReset: () => void;
}

const sortOptions = [
  { key: "updated", label: "按更新时间" },
  { key: "rating", label: "按评分高低" },
  { key: "views", label: "按热度" },
];

export default function FilterSidebar({
  genres,
  activeGenre,
  activeYear,
  sort,
  onGenreChange,
  onYearChange,
  onSortChange,
  onReset,
}: FilterSidebarProps) {
  const [open, setOpen] = useState(false);

  const chip = (isActive: boolean) =>
    `rounded-full px-3 py-1.5 text-sm transition-colors whitespace-nowrap ${
      isActive
        ? "bg-primary-500 text-background-50 dark:text-foreground-950"
        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
    }`;

  return (
    <aside className="lg:w-64 lg:shrink-0">
      <div className="rounded-lg border border-background-200 bg-background-50 lg:sticky lg:top-24">
        <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
          <span className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
            <i className="ri-filter-3-line text-primary-600"></i>
            筛选条件
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-foreground-500 hover:text-accent-600"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              className="lg:hidden w-7 h-7 flex items-center justify-center rounded-md border border-background-300 text-foreground-700"
              aria-label="展开筛选"
            >
              <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
          </div>
        </div>

        <div className={`${open ? "block" : "hidden"} lg:block px-4 py-4 space-y-6`}>
          <div>
            <h4 className="mb-3 text-sm font-semibold text-foreground-800">类型</h4>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => onGenreChange("all")} className={chip(activeGenre === "all")}>
                全部
              </button>
              {genres.map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => onGenreChange(genre)}
                  className={chip(activeGenre === genre)}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold text-foreground-800">年份</h4>
            <div className="flex flex-wrap gap-2">
              {yearRanges.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  onClick={() => onYearChange(range.key)}
                  className={chip(activeYear === range.key)}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold text-foreground-800">排序</h4>
            <div className="flex flex-col gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onSortChange(option.key)}
                  className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                    sort === option.key
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {option.label}
                  {sort === option.key && <i className="ri-check-line text-primary-600"></i>}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}